/**
 * AiFeatureFlagService
 * Service for managing AI feature flags per vendor
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for AI feature flag CRUD operations with vendor and toggle endpoints
 */
export class AiFeatureFlagService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? AiFeatureFlagService.DEFAULT_SERVICE_PATH,
            basePath: AiFeatureFlagService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET BY VENDOR ====================
    /**
     * Get all feature flags for a vendor
     * @param vendorId - The vendor ID
     * @returns Array of feature flags for the vendor
     */
    async getByVendor(vendorId) {
        const response = await this.getByVendorRaw(vendorId);
        return response.data ?? [];
    }
    /**
     * Get all feature flags for a vendor with full response
     * @param vendorId - The vendor ID
     * @returns ResponseData with array of feature flags
     */
    getByVendorRaw(vendorId) {
        const url = this.buildUrl(`vendor/${vendorId}`);
        return this.http.get(url);
    }
    // ==================== GET BY VENDOR FEATURE ====================
    /**
     * Get a specific feature flag for a vendor by feature type
     * @param vendorId - The vendor ID
     * @param featureType - The feature type
     * @returns The feature flag
     * @throws Error if not found
     */
    async getByVendorFeature(vendorId, featureType) {
        const response = await this.getByVendorFeatureRaw(vendorId, featureType);
        if (!response.data) {
            throw new Error(`Feature flag for vendor ${vendorId} and type ${featureType} not found`);
        }
        return response.data;
    }
    /**
     * Get a specific feature flag for a vendor by feature type with full response
     * @param vendorId - The vendor ID
     * @param featureType - The feature type
     * @returns ResponseData with the feature flag
     */
    getByVendorFeatureRaw(vendorId, featureType) {
        const url = this.buildUrl(`vendor/${vendorId}/${featureType}`);
        return this.http.get(url);
    }
    // ==================== TOGGLE ====================
    /**
     * Toggle the enabled state of a feature flag
     * @param id - The feature flag ID
     * @param isEnabled - Whether to enable or disable the feature
     * @returns The updated feature flag
     * @throws Error if toggle failed
     */
    async toggle(id, isEnabled) {
        const response = await this.toggleRaw(id, isEnabled);
        if (!response.data) {
            throw new Error(`Failed to toggle feature flag with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Toggle the enabled state of a feature flag with full response
     * @param id - The feature flag ID
     * @param isEnabled - Whether to enable or disable the feature
     * @returns ResponseData with the updated feature flag
     */
    toggleRaw(id, isEnabled) {
        const url = this.buildUrl(`${id}/toggle`);
        return this.http.patch(url, { isEnabled });
    }
}
AiFeatureFlagService.DEFAULT_SERVICE_PATH = 'ai-management';
AiFeatureFlagService.BASE_PATH = 'api/ai/feature-flags';
//# sourceMappingURL=AiFeatureFlagService.js.map